import type {FC} from 'react'
import * as React from 'react'
import {Box, Skeleton} from '@mui/material'
import {DataGrid, useGridApiRef, type GridColDef, type GridRowModel} from '@mui/x-data-grid'
import type {Element, ElementContent, Properties, Text} from 'hast'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import {useCustomTranslation} from '@hooks/useCustomTranslation.ts'
import {useSnackbar} from '@hooks/useSnackbar'
import {copyToClipboard} from '@shared/utils/clipboard'
import {neutralizeCsvCell} from '@shared/utils/csvSafety'
import {useResizeGrip} from '@containers/Dashboards/AgentChat/components/messages/useResizeGrip'
import ActionsMenu, {
  type ActionMenuItem,
} from '@containers/Dashboards/AgentChat/components/messages/ActionsMenu'

// The MIT DataGrid throws for a page size above 100, which took the whole app down
// through the error boundary for a 124-row agent table (AB#6662). Up to this many
// rows the table is one footer-less page; beyond it the footer pages through.
const MAX_PAGE_SIZE = 100

/**
 * Stable placeholder shown while a markdown table block is still streaming in. Like
 * the chart skeleton, it occupies a fixed shape so the turn doesn't shift layout as
 * rows arrive token-by-token -- we render nothing real until the whole table block
 * has landed, then swap to the grid below. Sits in the same bordered, rounded
 * container as the DataGrid so the swap is near-seamless.
 */
export const MarkdownTableSkeleton: FC<{rows?: number}> = ({rows = 4}) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      my: 1,
      p: 1,
    }}
  >
    <Skeleton variant="rounded" width="100%" height={32} sx={{borderRadius: 0.5}} />
    {Array.from({length: rows}).map((_, i) => (
      <Skeleton key={i} variant="text" width="100%" height={28} sx={{mt: 0.5}} />
    ))}
  </Box>
)

function slugifyFileName(input: string): string {
  return (
    input
      // explicit German mappings first (preserve intent better than generic diacritics strip)
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/Ä/g, 'Ae')
      .replace(/Ö/g, 'Oe')
      .replace(/Ü/g, 'Ue')
      .replace(/ß/g, 'ss')
      // normalize & drop remaining diacritics (safety net for other languages)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      // lower, spaces → underscore
      .toLowerCase()
      .replace(/\s+/g, '_')
      // keep only safe filename chars
      .replace(/[^a-z0-9._-]/g, '')
      // collapse repeats
      .replace(/_+/g, '_')
      .replace(/^-+|-+$/g, '')
      .slice(0, 120)
  ) // optional length cap
}

type WithType = {type: string}

function hasType(n: unknown): n is WithType {
  return (
    typeof n === 'object' &&
    n !== null &&
    'type' in (n as object) &&
    typeof (n as {type?: unknown}).type === 'string'
  )
}

/** Type guards for HAST (no `any`) */
export function isElement(n: ElementContent | null | undefined): n is Element {
  return hasType(n) && n.type === 'element'
}

export function isText(n: ElementContent | null | undefined): n is Text {
  return hasType(n) && n.type === 'text'
}

/** Safe text extraction from a HAST node (Element | Text) */
function textContent(node?: ElementContent | null): string {
  if (!node) return ''
  if (isText(node)) return node.value ?? ''
  if (isElement(node)) {
    const kids = (node.children ?? []) as ElementContent[]
    return kids.map(textContent).join('')
  }
  return ''
}

type Align = 'left' | 'center' | 'right'

/** Convert a HAST <table> element into DataGrid columns + rows */
function toGrid(node: Element): {columns: GridColDef[]; rows: GridRowModel[]} {
  // find thead/tbody
  const kids = (node.children ?? []) as ElementContent[]
  const thead = kids.find((c) => isElement(c) && c.tagName === 'thead') as Element | undefined
  const tbody = kids.find((c) => isElement(c) && c.tagName === 'tbody') as Element | undefined

  /** headers **/
  let headers: {label: string; align?: Align}[] = []
  if (thead) {
    const tr = (thead.children as ElementContent[]).find(
      (c) => isElement(c) && c.tagName === 'tr',
    ) as Element | undefined
    const ths = (tr?.children ?? []).filter((c) => isElement(c) && c.tagName === 'th') as Element[]
    headers = ths.map((th, i) => {
      const props = (th.properties ?? {}) as Properties & {align?: Align}
      const align: Align =
        props.align === 'center' ? 'center' : props.align === 'right' ? 'right' : 'left'
      return {
        label: (textContent(th) || `Col ${i + 1}`).trim(),
        align,
      }
    })
  }

  /** rows **/
  const trs = (tbody?.children ?? []).filter((c) => isElement(c) && c.tagName === 'tr') as Element[]

  const rows: GridRowModel[] = trs.map((tr, rIdx) => {
    const tds = (tr.children ?? []).filter((c) => isElement(c) && c.tagName === 'td') as Element[]
    const obj: GridRowModel = {id: rIdx + 1}
    headers.forEach((_, i) => {
      obj[`c${i}`] = textContent(tds[i] as ElementContent).trim()
    })
    // If the first header is '#', store the row index now
    if (headers[0]?.label?.trim() === '#') {
      obj['c0'] = String(rIdx + 1)
    }
    return obj
  })

  /** columns **/
  const columns: GridColDef[] = headers.map((h, i) => ({
    field: `c${i}`,
    headerName: h.label,
    align: h.align,
    headerAlign: h.align,
    // Widths come from content via autosizeOnMount (below) — no per-column cap, so a
    // long cell widens its column (the table then fills the bubble and scrolls).
    sortable: true,
  }))

  // Optional: narrow, non-sortable index column
  if (headers[0]?.label?.trim() === '#') {
    columns[0] = {...columns[0], width: 60, minWidth: 50}
  }

  return {columns, rows}
}

/**
 * Build a 2D array from columns/rows (WYSIWYG export). Every cell is passed through
 * `neutralizeCsvCell` because the table text comes from the LLM (and can be
 * prompt-injected): a cell starting with `=`, `+`, `-`, `@`, tab or CR would otherwise
 * be evaluated as a formula when the export is opened or pasted into a spreadsheet
 * (AB#6304). Display in the chat is unaffected -- this only feeds the exports.
 */
function toAoA(columns: GridColDef[], rows: GridRowModel[]): string[][] {
  const headerRow = columns.map((c) => c.headerName ?? c.field)
  const dataRows = rows.map((r) =>
    columns.map((c) => {
      const v = (r as Record<string, unknown>)[c.field]
      return v == null ? '' : String(v)
    }),
  )
  return [headerRow, ...dataRows].map((row) => row.map(neutralizeCsvCell))
}

/** CSV text for the "Export as CSV" action (formula-neutralized, RFC 4180 quoting). */
export function buildCsv(columns: GridColDef[], rows: GridRowModel[]): string {
  return toAoA(columns, rows)
    .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
    .join('\n')
}

/** Tab-separated text for "Copy to clipboard" (formula-neutralized), for spreadsheet paste. */
export function buildTsv(columns: GridColDef[], rows: GridRowModel[]): string {
  return toAoA(columns, rows)
    .map((row) => row.join('\t'))
    .join('\n')
}

/** CSV export (no deps) */
function downloadCsv(
  columns: GridColDef[],
  rows: GridRowModel[],
  filename = 'table-export.csv',
): void {
  const csv = buildCsv(columns, rows)
  const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'})
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const MarkdownTableGrid: FC<{node: Element; title?: string}> = ({node, title}) => {
  const [t] = useCustomTranslation('alpinist')
  const [showNotification] = useSnackbar()

  const signature = React.useMemo(() => {
    // stable-ish: headers + all cell text
    return textContent(node)
  }, [node]) // node changes → signature recomputed, but now we can use signature as dependency elsewhere

  const {columns, rows} = React.useMemo(() => toGrid(node), [signature])

  const handleExportCsv = React.useCallback(() => {
    const base = title?.trim()
      ? slugifyFileName(title.trim())
      : `alpinist-table-${new Date().toISOString().slice(0, 10)}`
    const safeName = base || `alpinist-table-${new Date().toISOString().slice(0, 10)}`
    downloadCsv(columns, rows, `${safeName}.csv`)
  }, [columns, rows, title])

  const handleCopyToClipboard = React.useCallback(() => {
    // Tab-separated so it pastes straight into spreadsheet cells (Excel/Sheets).
    const tsv = buildTsv(columns, rows)
    void copyToClipboard(tsv).then((ok) =>
      showNotification({
        message: ok ? t('table_copied') : t('table_copy_failed'),
        severity: ok ? 'success' : 'error',
      }),
    )
  }, [columns, rows, showNotification, t])

  const actions: ActionMenuItem[] = [
    {
      key: 'export-csv',
      label: t('export_as_csv'),
      icon: <DownloadOutlinedIcon fontSize="small" />,
      onClick: handleExportCsv,
    },
    {
      key: 'copy-clipboard',
      label: t('copy_to_clipboard'),
      icon: <ContentCopyOutlinedIcon fontSize="small" />,
      onClick: handleCopyToClipboard,
    },
  ]

  // Cap the table to the autosized columns' total so a narrow table sizes to its
  // content instead of stretching full-width with an empty filler column. Once the
  // columns would overflow, the cap exceeds the column width and the table fills the
  // bubble (and scrolls). Falls back to full-width if measurement isn't available.
  const apiRef = useGridApiRef()
  const [fitWidth, setFitWidth] = React.useState<number | undefined>(undefined)
  // The resizable outer container, plus the content signature it was last auto-sized
  // for -- so we apply the initial fit-to-content size ONCE and then leave the user's
  // manual resize (CSS `resize: both`) untouched on re-render.
  const boxRef = React.useRef<HTMLDivElement | null>(null)
  const sizedSigRef = React.useRef<string | null>(null)
  React.useEffect(() => {
    const api = apiRef.current
    if (!api?.autosizeColumns) return undefined
    let alive = true
    void api
      .autosizeColumns({includeHeaders: true, includeOutliers: true})
      .then(() => {
        if (!alive) return
        const total = api.getAllColumns().reduce((sum, c) => sum + c.computedWidth, 0)
        // A tall table shows a vertical scrollbar, which eats horizontal space the
        // autosized columns already claim -- the grid then thinks it overflows and
        // shows a phantom HORIZONTAL scrollbar even though the content fits. Reserve
        // the scrollbar's width in the cap ONLY when a vertical scrollbar is present,
        // so columns + V-scrollbar fit exactly: no phantom H-scroll, and no empty gap
        // on short tables that have no vertical scrollbar.
        const dims = api.getRootDimensions?.()
        const vScrollbar = dims?.hasScrollY ? (dims.scrollbarSize ?? 0) : 0
        if (total > 0) setFitWidth(Math.ceil(total) + 4 + vScrollbar) // + grid borders (+ V-scrollbar)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [apiRef, signature])

  // The fit-to-content default size (width from the autosized columns, height from the
  // row count). Applied once initially and re-applied on a double-click of the grip.
  const applyDefaultSize = React.useCallback(() => {
    const el = boxRef.current
    if (!el || !fitWidth) return
    const ROW_H = 36 // compact density row height
    const initialHeight = Math.min(40 + rows.length * ROW_H + 4, 520)
    el.style.width = `${fitWidth}px`
    el.style.height = `${initialHeight}px`
  }, [fitWidth, rows.length])

  // Apply the initial fit-to-content size ONCE per table (keyed by content signature).
  // After that the user can drag the bottom-right grip to resize width/height freely;
  // we never overwrite their size on subsequent re-renders.
  React.useEffect(() => {
    if (!boxRef.current || !fitWidth || sizedSigRef.current === signature) return
    applyDefaultSize()
    sizedSigRef.current = signature
  }, [applyDefaultSize, fitWidth, signature])

  // Free resize via a shared corner grip (same grip + double-click-to-reset as the
  // charts). Double-click restores the fit-to-content default rather than the CSS auto
  // size, so the table snaps back to the same size it first rendered at.
  useResizeGrip(boxRef, {mode: 'free', onReset: applyDefaultSize})

  const pageSize = Math.min(rows.length || 1, MAX_PAGE_SIZE)

  return (
    <Box
      ref={boxRef}
      sx={{
        position: 'relative',
        // The grid fills the box and manages its own scrolling; keep the box from
        // scrolling so the absolutely-positioned resize grip stays pinned to the corner.
        overflow: 'auto',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        my: 1,
        // Initial width/height are set to fit content (see effect above); the user can
        // then drag the grip to resize. Never exceed the bubble width; keep a sane min.
        maxWidth: '100%',
        minWidth: 160,
        minHeight: 96,
      }}
    >
      {/* Actions menu floated in the top-right corner (shared with the charts) so the
          table can grow to the full column width without the control clipping. */}
      <ActionsMenu items={actions} tooltip={t('table_options')} />
      <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <DataGrid
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          density="compact"
          sx={{flex: 1, minHeight: 0}}
          // Autosize only measures the rows currently in the DOM. With virtualization
          // on, off-screen rows (e.g. a long description far down the list) are never
          // measured, so their column stays too narrow and the content is clipped.
          // These are small agent-generated tables, so render every row while sizing
          // (guarded for a pathological huge table) -- autosize then fits all content.
          disableVirtualization={rows.length <= 500}
          autosizeOnMount
          autosizeOptions={{includeHeaders: true, includeOutliers: true}}
          disableRowSelectionOnClick
          hideFooter={rows.length <= MAX_PAGE_SIZE}
          pageSizeOptions={[pageSize]}
          initialState={{
            pagination: {paginationModel: {pageSize, page: 0}},
          }}
        />
      </div>
    </Box>
  )
}

export default MarkdownTableGrid
